import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext.jsx";
import { formatPrice, WHATSAPP_NUMBER, LAUNCH_PLAN_ID } from "../data/products.js";
import { shortOrderId, paymentBadge, canCancelOrder } from "../api/client.js";
import AddressBook from "../components/AddressBook.jsx";
import MembershipCard from "../components/MembershipCard.jsx";
import Seo from "../components/Seo.jsx";
import { initialsOf } from "../utils/name.js";

// Inline stroke icons — consistent weight with the header's, and they inherit
// currentColor so the active nav state tints them for free.
const icon = (paths) => (
  <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor"
       strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {paths}
  </svg>
);
const IconOrders = icon(
  <>
    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
    <path d="m3.3 7 8.7 5 8.7-5" />
    <path d="M12 22V12" />
  </>
);
const IconUser = icon(
  <>
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </>
);
const IconSignOut = icon(
  <>
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <path d="m16 17 5-5-5-5" />
    <path d="M21 12H9" />
  </>
);

// Fulfilment status, shown alongside the payment badge — "is it paid" and
// "where is it" are two different questions and the card should answer both.
const STATUS_LABEL = {
  pending: "Order placed",
  confirmed: "Confirmed",
  processing: "Being packed",
  shipped: "Out for delivery",
  delivered: "Delivered",
  cancelled: "Cancelled",
};
const STATUS_TONE = {
  pending: "wait",
  confirmed: "wait",
  processing: "wait",
  shipped: "go",
  delivered: "done",
  cancelled: "off",
};
const statusKey = (o) => String(o?.status || "pending").toLowerCase();

export default function Account() {
  const { user, orders, logout, cancelOrder } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("orders");
  // Which order has its cancel panel open, plus its reason box and busy state.
  const [cancelling, setCancelling] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelBusy, setCancelBusy] = useState(false);
  const [cancelError, setCancelError] = useState("");
  const [cancelNotice, setCancelNotice] = useState("");

  if (!user) {
    return (
      <section className="section">
        <Seo title="My Account" path="/account" noindex />
        <div className="container cart-empty">
          <div className="cart-empty__icon">👤</div>
          <h2>You&apos;re not signed in</h2>
          <p>Sign in to view your orders and saved details.</p>
          <div className="hero__actions" style={{ justifyContent: "center" }}>
            <Link to="/login" className="btn btn--primary btn--lg">Sign in</Link>
            <Link to="/signup" className="btn btn--ghost btn--lg">Create account</Link>
          </div>
        </div>
      </section>
    );
  }

  const doLogout = async () => {
    await logout();
    navigate("/");
  };

  const fmtDate = (iso) =>
    new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });

  // Profile details aren't self-editable yet, so "change" opens a pre-filled
  // WhatsApp message to the farm instead of a form that goes nowhere.
  const changeRequest = (field) =>
    `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
      `Hi Rimmon Farm, I'd like to update my ${field} on my account (${user.email}).`
    )}`;

  const openCancel = (id) => {
    setCancelling(id);
    setCancelReason("");
    setCancelError("");
    setCancelNotice("");
  };

  const confirmCancel = async (order) => {
    setCancelBusy(true);
    setCancelError("");
    const res = await cancelOrder(order.id, cancelReason.trim() || undefined);
    setCancelBusy(false);
    if (!res.ok) {
      setCancelError(res.error || "Could not cancel this order. Please try again.");
      return;
    }
    setCancelling(null);
    setCancelNotice(
      res.order?.paymentStatus === "refunded"
        ? `Order ${shortOrderId(order.id)} cancelled. Your refund is on its way — 5–7 working days.`
        : `Order ${shortOrderId(order.id)} cancelled. A confirmation email is on its way.`
    );
  };

  return (
    <section className="section">
      <Seo title="My Account" path="/account" noindex />
      <div className="container">
        <header className="account__header">
          <h1>My account</h1>
          <p>Track your orders, manage delivery addresses and your profile.</p>
        </header>

        <MembershipCard />

        <div className="account">
        <aside className="account__side">
          <div className="account__greet">
            <span className="account__avatar">{initialsOf(user.name)}</span>
            <div>
              <small>Hello,</small>
              <strong title={user.name}>{user.name}</strong>
            </div>
          </div>

          <nav className="account__nav">
            <button
              className={`account__nav-top ${tab === "orders" ? "is-active" : ""}`}
              onClick={() => setTab("orders")}
              aria-current={tab === "orders" ? "page" : undefined}
            >
              <span className="account__nav-icon">{IconOrders}</span>
              My orders
              {orders.length > 0 && <span className="account__nav-count">{orders.length}</span>}
              <span className="account__nav-chev" aria-hidden="true">›</span>
            </button>

            <div className="account__group">
              <div className="account__group-head">
                <span className="account__nav-icon">{IconUser}</span>
                Account settings
              </div>
              <button
                className={tab === "profile" ? "is-active" : ""}
                onClick={() => setTab("profile")}
                aria-current={tab === "profile" ? "page" : undefined}
              >
                Profile information
                <span className="account__nav-chev" aria-hidden="true">›</span>
              </button>
              <button
                className={tab === "addresses" ? "is-active" : ""}
                onClick={() => setTab("addresses")}
                aria-current={tab === "addresses" ? "page" : undefined}
              >
                Manage addresses
                <span className="account__nav-chev" aria-hidden="true">›</span>
              </button>
            </div>
          </nav>

          <button className="account__logout" onClick={doLogout}>
            {IconSignOut} Sign out
          </button>
        </aside>

        <div className="account__main">
          {tab === "orders" && (
            <div className="account__panel">
              <div className="account__panel-head">
                <h2>My orders</h2>
                {orders.length > 0 && (
                  <span className="account__panel-meta">{orders.length} order{orders.length === 1 ? "" : "s"}</span>
                )}
              </div>
              {cancelNotice && <p className="order__cancel-notice">{cancelNotice}</p>}
              {orders.length === 0 ? (
                <div className="account__empty">
                  <span className="account__empty-icon">
                    <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor"
                         strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4H6z" />
                      <path d="M3 6h18" />
                      <path d="M16 10a4 4 0 0 1-8 0" />
                    </svg>
                  </span>
                  <h3>No orders yet</h3>
                  <p>Your farm-fresh orders will show up here once you place one.</p>
                  <Link to="/products" className="btn btn--primary">Start shopping</Link>
                </div>
              ) : (
                <div className="orders">
                  {orders.map((o) => {
                    const badge = paymentBadge(o);
                    const sk = statusKey(o);
                    const itemCount = o.lines.reduce((n, l) => n + (Number(l.qty) || 0), 0);
                    return (
                    <article className={`order ${sk === "cancelled" ? "order--off" : ""}`} key={o.id}>
                      <div className="order__head">
                        <div className="order__id">
                          <strong>Order {shortOrderId(o.id)}</strong>
                          <small>{fmtDate(o.date)} · {itemCount} item{itemCount === 1 ? "" : "s"}</small>
                        </div>
                        <div className="order__badges">
                          {/* When cancelled, the payment badge below already says
                              "Cancelled" (plus refund status where relevant) —
                              showing the fulfilment pill too just repeats it. */}
                          {sk !== "cancelled" && (
                            <span className={`order__status order__status--${STATUS_TONE[sk] || "wait"}`}>
                              {STATUS_LABEL[sk] || o.status}
                            </span>
                          )}
                          <span className={`order__badge order__badge--${badge.tone}`}>{badge.text}</span>
                        </div>
                      </div>
                      <ul className="order__lines">
                        {o.lines.map((l) => (
                          <li key={l.id}>
                            <span>{l.name} × {l.qty}</span>
                            <span>{formatPrice(l.lineTotal)}</span>
                          </li>
                        ))}
                      </ul>
                      <div className="order__foot">
                        <span>Total</span>
                        <strong>{formatPrice(o.total)}</strong>
                      </div>

                      {o.cancelReason && (
                        <p className="order__note"><strong>Cancelled:</strong> {o.cancelReason}</p>
                      )}

                      {canCancelOrder(o) && !o.lines.some((l) => l.id === LAUNCH_PLAN_ID) && cancelling !== o.id && (
                        <div className="order__actions">
                          <button className="btn btn--ghost btn--sm" onClick={() => openCancel(o.id)}>
                            Cancel order
                          </button>
                        </div>
                      )}

                      {cancelling === o.id && (
                        <div className="order__cancel">
                          <label htmlFor={`reason-${o.id}`}>Why are you cancelling? (optional)</label>
                          <textarea
                            id={`reason-${o.id}`}
                            rows={2}
                            value={cancelReason}
                            onChange={(e) => setCancelReason(e.target.value)}
                            maxLength={300}
                            placeholder="e.g. ordered by mistake, changed my mind"
                          />
                          <p className="order__cancel-hint">
                            If you paid online, we&apos;ll refund the full amount to your original payment
                            method within 5–7 working days.
                          </p>
                          {cancelError && <p className="order__cancel-error">{cancelError}</p>}
                          <div className="order__actions">
                            <button
                              className="btn btn--danger btn--sm"
                              onClick={() => confirmCancel(o)}
                              disabled={cancelBusy}
                            >
                              {cancelBusy ? "Cancelling…" : "Confirm cancellation"}
                            </button>
                            <button
                              className="btn btn--ghost btn--sm"
                              onClick={() => setCancelling(null)}
                              disabled={cancelBusy}
                            >
                              Keep order
                            </button>
                          </div>
                        </div>
                      )}
                    </article>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {tab === "addresses" && (
            <div className="account__panel">
              <div className="account__panel-head">
                <h2>Saved addresses</h2>
              </div>
              <AddressBook />
            </div>
          )}

          {tab === "profile" && (
            <div className="account__panel">
              <div className="account__panel-head">
                <h2>Profile information</h2>
              </div>

              {/* Read-only for now: there's no self-serve edit endpoint, so
                  each section links to WhatsApp rather than a dead "Edit". */}
              <div className="pf">
                <div className="pf__sec">
                  <div className="pf__sec-head">
                    <h3>Personal information</h3>
                    <a className="pf__edit" href={changeRequest("name")} target="_blank" rel="noopener noreferrer">
                      Request change
                    </a>
                  </div>
                  <div className="pf__box">{user.name}</div>
                </div>

                <div className="pf__sec">
                  <div className="pf__sec-head">
                    <h3>Email address</h3>
                    <a className="pf__edit" href={changeRequest("email address")} target="_blank" rel="noopener noreferrer">
                      Request change
                    </a>
                  </div>
                  <div className="pf__box">{user.email}</div>
                </div>

                <div className="pf__sec">
                  <div className="pf__sec-head">
                    <h3>Mobile number</h3>
                    <a className="pf__edit" href={changeRequest("mobile number")} target="_blank" rel="noopener noreferrer">
                      {user.phone ? "Request change" : "Add number"}
                    </a>
                  </div>
                  <div className={`pf__box ${user.phone ? "" : "pf__box--empty"}`}>
                    {user.phone || "Not added yet"}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        </div>
      </div>
    </section>
  );
}
